import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEO/SEOHead';
import { BlogCategory, BlogPost } from '../types/Blog';
import { loadLiveBlogData } from '../utils/BlogAPI';
import { categoryNames, formatStoryDate, postCategorySlugs, publishedPosts } from '../utils/blogPresentation';
import { journalListDelivery } from '../utils/imageSeo';
import './BlogJournal.css';

const FALLBACK_CATEGORIES: BlogCategory[] = [
  { id: 'family', name: 'Family', slug: 'family', seoTitle: 'Family Photography Blog | Cape Town Photographer' },
  { id: 'business-headshots', name: 'Business & Headshots', slug: 'business-headshots', seoTitle: 'Business & Headshot Photography Blog | Cape Town' },
  { id: 'lifestyle', name: 'Lifestyle', slug: 'lifestyle', seoTitle: 'Lifestyle Photography Blog | Cape Town Photographer' },
  { id: 'natural', name: 'Natural', slug: 'natural', seoTitle: 'Natural Light Photography Blog | Cape Town' },
];

const PAGE_SIZE = 12;

interface PageBootstrap {
  kind?: string;
  page?: number;
  posts?: BlogPost[];
  categories?: BlogCategory[];
  category?: BlogCategory;
}

function readPageBootstrap(): PageBootstrap | null {
  if (typeof document === 'undefined') return null;
  const node = document.getElementById('page-bootstrap');
  if (!node?.textContent) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
}

function listingFromBootstrap(categorySlug: string, page: number): PageBootstrap | null {
  const data = readPageBootstrap();
  if (!data || Number(data.page) !== page) return null;
  if (!categorySlug && data.kind === 'index' && Array.isArray(data.posts)) return data;
  if (categorySlug && data.kind === 'category' && data.category?.slug === categorySlug && Array.isArray(data.posts)) return data;
  return null;
}

function categoriesFromBootstrap(data: PageBootstrap | null): BlogCategory[] {
  if (!data) return [];
  if (Array.isArray(data.categories) && data.categories.length) return data.categories;
  if (data.category) return [data.category];
  return [];
}

const BlogPage: React.FC = () => {
  const { category: categorySlug = '', page: pageParam = '1' } = useParams();
  const page = Math.max(1, Number(pageParam) || 1);
  const seeded = listingFromBootstrap(categorySlug, page);
  const bootstrapPosts = seeded?.posts;
  const seededPosts = Array.isArray(bootstrapPosts) ? bootstrapPosts : [];
  const [posts, setPosts] = useState<BlogPost[]>(seededPosts);
  const [categories, setCategories] = useState<BlogCategory[]>(categoriesFromBootstrap(seeded));
  const [ready, setReady] = useState(seededPosts.length > 0);
  const [usingServerPage, setUsingServerPage] = useState(seededPosts.length > 0);

  useEffect(() => {
    let active = true;
    loadLiveBlogData()
      .then(({ posts: loaded, categories: loadedCategories }) => {
        if (!active) return;
        const published = publishedPosts(loaded || []);
        const relevant = categorySlug
          ? published.filter((post) => postCategorySlugs(post).includes(categorySlug))
          : published;
        if (relevant.length) {
          setPosts(published);
          setUsingServerPage(false);
        } else if (!seededPosts.length) {
          setPosts([]);
          setUsingServerPage(false);
        }
        if (Array.isArray(loadedCategories) && loadedCategories.length) setCategories(loadedCategories);
        setReady(true);
      })
      .catch(() => {
        if (!active) return;
        if (!seededPosts.length) {
          setPosts([]);
          setUsingServerPage(false);
        }
        setReady(true);
      });
    return () => {
      active = false;
    };
  }, [categorySlug, page, seededPosts.length]);

  const source = categories.length ? categories : FALLBACK_CATEGORIES;
  const linked = source.filter((category) => {
    const title = String(category.seoTitle || '').trim();
    const hasPosts = posts.some((post) => postCategorySlugs(post).includes(category.slug));
    return Boolean(title && hasPosts);
  });
  const filters = [
    { name: 'All', slug: '', path: '/blog' },
    ...linked.map((category) => ({
      name: category.name,
      slug: category.slug,
      path: `/blog/category/${category.slug}`,
    })),
  ];
  const filter = filters.find((item) => item.slug === categorySlug);
  const category = categories.find((item) => item.slug === categorySlug) || (filter?.slug
    ? { id: filter.slug, name: filter.name, slug: filter.slug }
    : undefined);
  const filtered = categorySlug
    ? posts.filter((post) => postCategorySlugs(post).includes(categorySlug))
    : posts;
  const pages = usingServerPage ? 1 : Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const slice = usingServerPage ? filtered : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const base = categorySlug ? `/blog/category/${categorySlug}` : '/blog';
  const canonicalPath = page > 1 ? `${base}/page/${page}` : base;
  const title = category
    ? (category.seoTitle || `${category.name} | Cape Town Photographer`)
    : 'Photography Blog | Cape Town Photographer';
  const description = category
    ? (category.metaDescription || category.intro || '')
    : 'Stories and photographs from Carmen Lorraine in Cape Town. Family, business, lifestyle, and natural-light sessions.';
  const heading = category ? (category.h1 || category.name) : 'Blog';
  const categoryIndexable = !categorySlug || Boolean(String(category?.seoTitle || '').trim() && filtered.length);

  return (
    <div className="blog-page">
      <SEOHead
        exactTitle
        title={title}
        description={description}
        canonicalUrl={`https://carmenlorraine.com${canonicalPath}`}
        url={`https://carmenlorraine.com${canonicalPath}`}
        type="website"
        noIndex={Boolean(categorySlug) && ready && !categoryIndexable}
      />
      <main className="page-content journal-index">
        <h1>{heading}</h1>
        <p className="journal-standfirst">
          {category?.intro || 'Stories and photographs from sessions in Cape Town.'}
        </p>
        <nav className="journal-filters" aria-label="Categories">
          {filters.map((filter) => (
            <Link
              key={filter.path}
              to={filter.path}
              aria-current={filter.slug === categorySlug ? 'page' : undefined}
            >
              {filter.name}
            </Link>
          ))}
        </nav>
        <div className="journal-grid">
          {slice.map((post) => {
            const source = post.heroImage?.secure_url || post.heroImage?.url || '';
            const photo = source ? journalListDelivery(source, post.heroImage) : null;
            const names = category ? [category.name] : categoryNames(post, categories);
            const label = [names.filter(Boolean).join(' · '), formatStoryDate(post.publishedAt)].filter(Boolean).join(' · ');
            return (
              <Link className="journal-card" key={post.id} to={`/blog/${post.slug}`}>
                {photo ? (
                  <span className="journal-card-media">
                    <img
                      className="journal-card-photo"
                      src={photo.src || source}
                      srcSet={photo.srcSet || undefined}
                      sizes={photo.sizes}
                      alt={post.heroImage?.alt || post.featuredImageAlt || post.title}
                      width={photo.width}
                      height={photo.height}
                      loading="lazy"
                      decoding="async"
                    />
                  </span>
                ) : null}
                {label ? <p className="journal-kicker">{label}</p> : null}
                <h2>{post.title}</h2>
              </Link>
            );
          })}
        </div>
        {ready && slice.length === 0 ? <p className="journal-empty">New stories are on the way.</p> : null}
        {pages > 1 ? (
          <nav className="journal-pager" aria-label="Pagination">
            {page > 1 ? <Link to={page === 2 ? base : `${base}/page/${page - 1}`}>Previous</Link> : null}
            <span>Page {page} of {pages}</span>
            {page < pages ? <Link to={`${base}/page/${page + 1}`}>Next</Link> : null}
          </nav>
        ) : null}
      </main>
    </div>
  );
};

export default BlogPage;
